"use client"

import { useState, useEffect, useMemo, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { ArrowLeft } from "lucide-react"
import { useUser } from "@/lib/community/hooks/use-user"
import type { CommunityPost } from "@/lib/community/types"
import { useAppContext } from "@/lib/app-context"
import { useAuthContext } from "@/lib/auth-context"
import { ProfileHeroSection } from "./profile/profile-hero-section"
import { ProfileStatsSection } from "./profile/profile-stats-section"
import { ProfilePostsSection } from "./profile/profile-posts-section"
import { ProfileFavoritesSection } from "./profile/profile-favorites-section"
import { ProfileRecentActivitySection } from "./profile/profile-recent-activity-section"
import { BadgeWallModal } from "./profile/badge-wall-modal"
import { FollowerListModal } from "./profile/follower-list-modal"
import { VipSubscribeModal } from "./profile/vip-subscribe-modal"
import { BindPhoneModal } from "./profile/bind-phone-modal"
import { PostDetailModal } from "./post-detail-modal"
import { ProfilePageSkeleton } from "./profile/profile-skeleton"
import "./profile/profile-page.less"

/** 临时、可撤销：隐藏用户资料页的“返回社区”悬浮按钮；恢复时改回 true。 */
const SHOW_COMMUNITY_BACK_BUTTON = false

/** 临时、可撤销：隐藏用户资料页底部导航；恢复时改回 true。 */
const SHOW_PROFILE_FOOTER = false

/** Staggered entrance: blur + scale + translateY, matching LandingPage pattern */
const makeEnterStyle = (entered: boolean) => (delay: number): React.CSSProperties => ({
  opacity: entered ? 1 : 0,
  transform: entered ? "translateY(0) scale(1)" : "translateY(20px) scale(0.97)",
  filter: entered ? "blur(0)" : "blur(6px)",
  transition: `opacity 600ms ${delay}ms cubic-bezier(.16,1,.3,1), transform 600ms ${delay}ms cubic-bezier(.16,1,.3,1), filter 600ms ${delay}ms cubic-bezier(.16,1,.3,1)`,
})

export function UserProfilePage({
  userId,
  onClaimBenefits,
}: {
  userId: string
  onClaimBenefits?: () => void
}) {
  const navigate = useNavigate()
  const { setWaveIntensity } = useAppContext()
  const { isLoggedIn, openLogin, currentUser } = useAuthContext()
  const { user, loading: profileLoading } = useUser(userId)
  const [selectedPost, setSelectedPost] = useState<CommunityPost | null>(null)
  const [showBadgeWall, setShowBadgeWall] = useState(false)
  const [showVipModal, setShowVipModal] = useState(false)
  const [showBindPhoneModal, setShowBindPhoneModal] = useState(false)
  const [boundPhone, setBoundPhone] = useState<string | null>(currentUser?.phone ?? null)
  const [followerModal, setFollowerModal] = useState<"followers" | "following" | null>(null)
  const [entered, setEntered] = useState(false)
  const pageRef = useRef<HTMLDivElement>(null)

  const openFollowers = () => {
    if (!isLoggedIn) { openLogin(); return }
    setFollowerModal("followers")
  }
  const openFollowing = () => {
    if (!isLoggedIn) { openLogin(); return }
    setFollowerModal("following")
  }

  const isOwnProfile = userId === "u-self"

  useEffect(() => {
    setWaveIntensity(0.2)
    return () => setWaveIntensity(1)
  }, [setWaveIntensity])

  useEffect(() => {
    if (!profileLoading && user) {
      requestAnimationFrame(() => {
        pageRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" })
        setEntered(true)
      })
    }
  }, [profileLoading, user, userId])

  useEffect(() => {
    setEntered(false)
    pageRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" })
  }, [userId])

  useEffect(() => {
    setBoundPhone(currentUser?.phone ?? null)
  }, [currentUser?.phone])

  const enterStyle = useMemo(() => makeEnterStyle(entered), [entered])

  if (profileLoading) {
    return <ProfilePageSkeleton />
  }

  if (!user) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center gap-3">
        <p className="text-muted-foreground text-sm">用户不存在</p>
        <button
          type="button"
          onClick={() => navigate("/community")}
          className="text-xs text-primary hover:underline"
        >
          返回社区
        </button>
      </div>
    )
  }

  return (
    <div ref={pageRef} className="profile-page h-full w-full overflow-y-auto">
      {SHOW_COMMUNITY_BACK_BUTTON && (
        <div className="profile-back-btn-container" style={enterStyle(0)}>
          <button
            type="button"
            onClick={() => navigate("/community")}
            className="profile-back-btn liquid-glass-float"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            返回社区
          </button>
        </div>
      )}

      {/* ── Full-screen Hero ── */}
      <div className="profile-page-shell">
        <ProfileHeroSection
          user={user}
          isOwnProfile={isOwnProfile}
          onOpenBadgeWall={() => setShowBadgeWall(true)}
          onOpenFollowing={openFollowing}
          onOpenVip={() => {
            if (!isLoggedIn) { openLogin(); return }
            setShowVipModal(true)
          }}
          boundPhone={isOwnProfile ? boundPhone : null}
          onOpenBindPhone={() => {
            if (!isLoggedIn) { openLogin(); return }
            setShowBindPhoneModal(true)
          }}
        />

        {/* ── Big Stats ── */}
        <ProfileStatsSection
          stats={user.stats}
          onOpenFollowers={openFollowers}
          onOpenFollowing={openFollowing}
        />

        {/* ── Content: single-column layout ── */}
        <div className="profile-content-area">
          <div className="profile-main-col">
            {/* ── Bento Posts Grid (hot works) ── */}
            <ProfilePostsSection
              userId={userId}
              onPostClick={setSelectedPost}
            />

            {/* ── Favorites horizontal scroll (own profile only) ── */}
            <ProfileFavoritesSection
              isOwnProfile={isOwnProfile}
              onPostClick={setSelectedPost}
            />

            {/* ── Recent Activity ── */}
            <ProfileRecentActivitySection userId={userId} />
          </div>
        </div>
      </div>

      {SHOW_PROFILE_FOOTER && (
        <footer className="profile-section-divider profile-footer">
          <div className="profile-footer-inner">
            <span className="profile-footer-brand">SAFECLAW</span>
            <div className="profile-footer-links">
              <button type="button" onClick={() => navigate("/community")}>社区</button>
              <button type="button" onClick={() => navigate("/courses")}>学院</button>
              <button type="button" onClick={() => navigate("/skills")}>技能市场</button>
            </div>
          </div>
        </footer>
      )}

      {/* ── Modals ── */}
      {selectedPost && (
        <PostDetailModal
          postId={selectedPost.id}
          onClose={() => setSelectedPost(null)}
        />
      )}

      {showBadgeWall && (
        <BadgeWallModal
          userId={userId}
          displayName={user.displayName}
          onClose={() => setShowBadgeWall(false)}
        />
      )}

      {followerModal && (
        <FollowerListModal
          userId={userId}
          displayName={user.displayName}
          initialTab={followerModal}
          onClose={() => setFollowerModal(null)}
        />
      )}

      {isOwnProfile && (
        <VipSubscribeModal
          isOpen={showVipModal}
          onClose={() => setShowVipModal(false)}
        />
      )}

      {isOwnProfile && showBindPhoneModal && (
        <BindPhoneModal
          onClose={() => setShowBindPhoneModal(false)}
          onSuccess={(phone) => {
            setBoundPhone(phone)
            setShowBindPhoneModal(false)
            onClaimBenefits?.()
          }}
        />
      )}
    </div>
  )
}
